import { useCallback, useEffect, useState } from 'react';

import { issues as issuesApi } from '@/helpers/ApiHelper';
import type { ListIssuesParams } from '@/helpers/ApiHelper';
import type { PaginatedIssues } from '@/types/issue';

interface UseIssuesResult {
  data: PaginatedIssues | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useIssues(params: ListIssuesParams = {}): UseIssuesResult {
  const [data, setData] = useState<PaginatedIssues | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Stable serialized key so the effect reruns only when params values change.
  const paramsKey = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await issuesApi.list(JSON.parse(paramsKey) as ListIssuesParams);
      setData(result);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setIsLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    issuesApi
      .list(JSON.parse(paramsKey) as ListIssuesParams)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [paramsKey]);

  return { data, isLoading, error, refetch: fetchData };
}
